import { downloadFile, filterObject } from "@/utils/helpers";
import { useAxios } from "@/utils/hooks/useAxios";
import { useSystem } from "@/utils/hooks/useLocation";
import { useParamFilters } from "@/utils/hooks/useParamFilters";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { toast } from "react-toastify";

/** List + mutation base paths for Makkah departure campaign vs group flows. */
export const MAKKA_DEPARTURE_API_BASE = {
    campaigns: "makkah/departure-campaigns",
    groups: "makkah/departure-group",
};

const makkahDepartureListBase = (currentSystem, listType) =>
    listType === "groups" ? `${currentSystem}/departure-group` : `${currentSystem}/departure-campaigns`;

// ========================
// Queries
// ========================

/** Single observer: use this on pages that switch campaigns vs groups to avoid duplicate requests. */
export const useMakkahCampaignOrGroupDepartureListQuery = (params, listType, options = {}) => {
    const { enabled = true } = options;
    const { axiosProtected } = useAxios();
    const { getAllParamFilters } = useParamFilters();
    const { currentSystem } = useSystem();

    const from_id = currentSystem === "makkah" ? 1 : 2;
    const { duration, ...query } = getAllParamFilters({ duration: "today", from_id, direction_id: -1 });

    const base = makkahDepartureListBase(currentSystem, listType);
    const urlTemp = `${base}/${duration}`;
    const url = urlTemp.replace("/all", "");

    const filteredParams = filterObject(params);
    const queryKey =
        listType === "groups"
            ? ["departure-group", filteredParams]
            : ["campaign-and-group-departure", filteredParams];

    return useQuery({
        queryKey,
        queryFn: () =>
            axiosProtected.get(url, {
                params: { ...filteredParams, ...query },
            }),
        enabled,
    });
};

export const useGetCampaignAndGroupDepartureQuery = (params, options = {}) =>
    useMakkahCampaignOrGroupDepartureListQuery(params, "campaigns", options);

export const useGetDepartureGroupQuery = (params, options = {}) =>
    useMakkahCampaignOrGroupDepartureListQuery(params, "groups", options);
export const useGetInitialDepartureDetailQuery = ({ enabled = true }) => {
    const { axiosProtected } = useAxios();
    const { id } = useParams();
    const { currentSystem } = useSystem();
    return useQuery({
        queryKey: ["initial-departure-detail", id],
        queryFn: () => axiosProtected.get(`${currentSystem}/initial-departure/departure-detail/${id}`),
        enabled,
    });
};
export const useGetInitiaCampaignAndGroupDepartureQuery = (params) => {
    const { axiosProtected } = useAxios();
    const filteredParams = filterObject(params)
    const { currentSystem } = useSystem();
    return useQuery({
        queryKey: ["campaign-and-group-departure", filteredParams],
        queryFn: () => axiosProtected.get(`${currentSystem}/initial-departure`, {
            params: filteredParams
        }),
    });
};
export const useGetInitialDeparturePilgrimsQuery = (params) => {
    const { axiosProtected } = useAxios();
    const filteredParams = filterObject(params)
    const { id: campaign_id } = useParams();
    const { currentSystem } = useSystem();
    return useQuery({
        queryKey: ["initial-departure-pilgrims", filteredParams, campaign_id],
        queryFn: () => axiosProtected.get(`${currentSystem}/initial-departure/departure-detail/${campaign_id}/pilgrims`, {
            params: filteredParams
        }),
    });
};

// ========================
// Mutations
// ========================
export const useMarkDepartureMutation = ({ listType = "campaigns" } = {}) => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { currentSystem } = useSystem();
    const base = makkahDepartureListBase(currentSystem, listType);

    return useMutation({
        mutationFn: (payload) => {
            return axiosProtected.post(`${base}/set-departure-time`, payload);
        },
        onSuccess: () => {
            toast.success(t("DEPARTURE_MARKED_SUCCESSFULLY"));
            queryClient.invalidateQueries({
                queryKey: [listType === "groups" ? "departure-group" : "campaign-and-group-departure"],
            });
        },
        onError: (error) => {
            toast.error(error.response.data.message || t("DEPARTURE_MARK_FAILED"));
        },
    });
};
export const useAssignInitialDepartureBusMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { currentSystem } = useSystem();
    return useMutation({
        mutationFn: (payload) => {
            return axiosProtected.post(`${currentSystem}/initial-departure/assign-pilgrims`, payload);
        },
        onSuccess: () => {
            toast.success(t("DEPARTURE_ASSIGNED_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["initial-departure-pilgrims"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message || t("DEPARTURE_ASSIGN_FAILED"));
        },
    });
};
export const useExportInitialDepartureMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const { currentSystem } = useSystem();
    return useMutation({
        mutationFn: (params) => axiosProtected.post(`${currentSystem}/reports/initial-departure`, null, {
            responseType: "blob",
            params: {
                ...params,
                format: "pdf",
            }
        }),
        onSuccess: (response) => {
            const blob = new Blob([response.data], {
                type: "application/pdf",
            });
            const filename = `departure-report.pdf`;
            downloadFile(blob, filename);
            toast.success(t("DEPARTURE_EXPORTED_SUCCESSFULLY"));
        },
        onError: (error) => {
            toast.error(error.response.data.message || t("DEPARTURE_EXPORT_FAILED"));
        },
    });
};
export const useStoreInitialDepartureMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { currentSystem } = useSystem();
    return useMutation({
        mutationFn: (payload) => {
            return axiosProtected.post(`makkah/initial-departure/store`, { ...payload });
        },
        onSuccess: () => {
            toast.success(t("DEPARTURE_STORED_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["initial-departure-detail"] });
        },
    });
};
export const useAssignInitialDeparturePilgrimsMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload) => {
            return axiosProtected.post(`makkah/initial-departure/assign-pilgrims`, { ...payload });
        },
        onSuccess: () => {
            toast.success(t("DEPARTURE_ASSIGN_PILGRIMS_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["initial-departure-pilgrims"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message || t("DEPARTURE_ASSIGN_PILGRIMS_FAILED"));
        },
    });
};

export const useCancelDepartureMutation = ({ listType = "campaigns" } = {}) => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { currentSystem } = useSystem();
    const base = makkahDepartureListBase(currentSystem, listType);

    return useMutation({
        mutationFn: (payload) => {
            return axiosProtected.post(`${base}/remove-departure-time`, payload);
        },
        onSuccess: () => {
            toast.success(t("DEPARTURE_CANCELLED_SUCCESSFULLY"));
            queryClient.invalidateQueries({
                queryKey: [listType === "groups" ? "departure-group" : "campaign-and-group-departure"],
            });
        },
        onError: (error) => {
            toast.error(error.response.data.message || t("DEPARTURE_CANCELLED_FAILED"));
        },
    });
};

